import { Navigate, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Fab,
  Fade,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  useScrollTrigger,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import CurrencyExchangeRoundedIcon from '@mui/icons-material/CurrencyExchangeRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';

import MetaDecorator from '../Apm/Other/MetaDecorator';
import base from '../Data/MetaData/base.json';
import { ContDePlataLogo_onwhite1 } from '../Data/imageURL';
import { appURL } from '../Data/baseURL';
import { maxNumberAllowedPerSubscription } from '../Data/generalData';
import Footer from '../Apm/Other/Footer';
import {
  GlassCard,
  PublicBackdrop,
  PublicContainer,
  SectionHeading,
  TechBadge,
  publicPalette,
} from '../Public/PublicLayout';

const featureIcons = [
  <ReceiptLongRoundedIcon />,
  <MailOutlineRoundedIcon />,
  <GroupsRoundedIcon />,
  <PublicRoundedIcon />,
  <DevicesRoundedIcon />,
  <SyncAltRoundedIcon />,
];

const ScrollTop = ({ children }) => {
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 500 });

  return (
    <Fade in={trigger}>
      <Box
        role="presentation"
        onClick={() => document.querySelector('#overview')?.scrollIntoView({ behavior: 'smooth' })}
        sx={{ position: 'fixed', right: { xs: 14, sm: 24 }, bottom: { xs: 14, sm: 24 }, zIndex: 20 }}
      >
        {children}
      </Box>
    </Fade>
  );
};

const DashboardPreview = ({ t }) => {
  const rows = [
    ['INV-1048', 'Northline Logistics', '€ 2,840', 'Sent'],
    ['INV-1047', 'Euro Transit Group', '€ 1,320', 'Paid'],
    ['Q-0312', 'Atlas Freight', '€ 4,650', 'Draft'],
    ['INV-1046', 'Delta Cargo', '€ 980', 'Overdue'],
  ];

  return (
    <GlassCard
      sx={{
        position: 'relative',
        p: { xs: 1.5, sm: 2 },
        overflow: 'hidden',
        transform: { md: 'perspective(1400px) rotateY(-5deg) rotateX(2deg)' },
        boxShadow: `0 38px 100px rgba(0,0,0,.46), 0 0 80px ${alpha(publicPalette.primary, 0.11)}`,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, pb: 1.75 }}>
        <Stack direction="row" spacing={0.75}>
          {['#FB7185', '#FBBF24', '#34D399'].map((color) => (
            <Box key={color} sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: color }} />
          ))}
        </Stack>
        <Chip
          size="small"
          icon={<CloudDoneRoundedIcon />}
          label={t('public.preview_live', { defaultValue: 'Live workspace' })}
          sx={{
            color: '#BFFBEA',
            backgroundColor: alpha(publicPalette.accent, 0.1),
            '& .MuiChip-icon': { color: publicPalette.accent },
          }}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '150px 1fr' },
          minHeight: 420,
          border: `1px solid ${publicPalette.border}`,
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: '#07101F',
        }}
      >
        <Stack
          spacing={1}
          sx={{
            display: { xs: 'none', sm: 'flex' },
            p: 2,
            borderRight: `1px solid ${publicPalette.border}`,
            backgroundColor: alpha('#FFFFFF', 0.018),
          }}
        >
          <Typography sx={{ mb: 1, color: publicPalette.text, fontWeight: 900, letterSpacing: '-.02em' }}>
            CONTDEPLATA
          </Typography>
          {[t('general.invoices'), t('general.quotes'), t('general.clients'), t('general.invoice_items')].map(
            (item, index) => (
              <Stack
                key={item}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: 2,
                  color: index === 0 ? publicPalette.text : publicPalette.muted,
                  backgroundColor: index === 0 ? alpha(publicPalette.primary, 0.12) : 'transparent',
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: index === 0 ? publicPalette.primary : alpha(publicPalette.muted, 0.5),
                  }}
                />
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{item}</Typography>
              </Stack>
            )
          )}
        </Stack>

        <Stack spacing={2} sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
            <Box>
              <Typography sx={{ color: publicPalette.text, fontWeight: 850, fontSize: 18 }}>
                {t('general.invoices')}
              </Typography>
              <Typography sx={{ color: publicPalette.muted, fontSize: 12 }}>
                {t('public.preview_subtitle', { defaultValue: 'Your document activity in real time' })}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              startIcon={<DescriptionRoundedIcon />}
              sx={{
                alignSelf: { xs: 'flex-start', sm: 'center' },
                borderRadius: 2,
                textTransform: 'none',
                background: `linear-gradient(110deg, ${publicPalette.primary}, ${publicPalette.secondary})`,
              }}
            >
              {t('button.add_invoice_lower')}
            </Button>
          </Stack>

          <Grid container spacing={1.25}>
            {[
              ['€ 18,790', t('public.preview_revenue', { defaultValue: 'Issued this month' }), '+18%'],
              ['24', t('public.preview_documents', { defaultValue: 'Documents' }), '+6'],
              ['92%', t('public.preview_paid', { defaultValue: 'Paid on time' }), '+4%'],
            ].map(([value, label, change]) => (
              <Grid item xs={12} sm={4} key={label}>
                <Box
                  sx={{
                    p: 1.4,
                    borderRadius: 2.5,
                    border: `1px solid ${publicPalette.border}`,
                    backgroundColor: alpha('#FFFFFF', 0.025),
                  }}
                >
                  <Typography sx={{ color: publicPalette.text, fontSize: 19, fontWeight: 850 }}>{value}</Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ color: publicPalette.muted, fontSize: 10 }}>{label}</Typography>
                    <Typography sx={{ color: publicPalette.accent, fontSize: 10, fontWeight: 800 }}>
                      {change}
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ overflow: 'hidden', borderRadius: 2.5, border: `1px solid ${publicPalette.border}` }}>
            {rows.map((row, index) => (
              <Box
                key={row[0]}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '0.8fr 1.7fr .8fr .7fr',
                  gap: 1,
                  px: 1.5,
                  py: 1.35,
                  borderBottom: index < rows.length - 1 ? `1px solid ${publicPalette.border}` : 'none',
                  backgroundColor: index % 2 ? alpha('#FFFFFF', 0.018) : 'transparent',
                }}
              >
                {row.map((cell, cellIndex) => (
                  <Typography
                    key={cell}
                    noWrap
                    sx={{
                      color:
                        cellIndex === 3
                          ? cell === 'Paid'
                            ? publicPalette.accent
                            : cell === 'Overdue'
                              ? '#FB7185'
                              : publicPalette.primary
                          : cellIndex === 1
                            ? publicPalette.muted
                            : publicPalette.text,
                      fontSize: { xs: 9, sm: 11 },
                      fontWeight: cellIndex === 0 || cellIndex === 3 ? 800 : 500,
                    }}
                  >
                    {cell}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>

          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Typography sx={{ color: publicPalette.muted, fontSize: 11 }}>
                {t('public.preview_goal', { defaultValue: 'Monthly collection goal' })}
              </Typography>
              <Typography sx={{ color: publicPalette.text, fontSize: 11, fontWeight: 800 }}>78%</Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={78}
              sx={{
                height: 7,
                borderRadius: 99,
                backgroundColor: alpha('#FFFFFF', 0.06),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 99,
                  background: `linear-gradient(90deg, ${publicPalette.primary}, ${publicPalette.accent})`,
                },
              }}
            />
          </Box>
        </Stack>
      </Box>
    </GlassCard>
  );
};

const Base = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isAuthLoading } = useSelector((state) => state.authSlice);

  if (isAuthLoading) {
    return (
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: '100%',
          minHeight: '100vh',
          backgroundColor: publicPalette.background,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) return <Navigate to={appURL} replace />;

  const features = [
    [t('base.title_customs_invoices'), t('base.subtitle_customs_invoices')],
    [
      t('base.send_by_email'),
      t('public.feature_email', {
        defaultValue: 'Deliver invoices and quotes directly from the workspace and keep the communication traceable.',
      }),
    ],
    [t('base.title_work_in_team'), t('base.subtitle_work_in_team')],
    [
      t('public.feature_multilingual', { defaultValue: 'Multilingual and multicurrency' }),
      t('public.feature_multilingual_text', {
        defaultValue: 'Create professional documents for international customers in multiple languages and currencies.',
      }),
    ],
    [t('base.title_first_component'), t('base.subtitle_first_component')],
    [t('base.title_1c_integration'), t('base.subtitle_1c_integration')],
  ];

  const workflow = [
    t('base.title_howitworks_advantage1'),
    t('base.title_howitworks_advantage2'),
    t('base.title_howitworks_advantage3'),
    t('base.title_howitworks_advantage4'),
  ];

  const plans = [
    { title: t('subscription.free'), price: 0, users: 2, quotes: 20, invoices: 20, clients: 20 },
    { title: t('subscription.pro'), price: 7, users: 5, quotes: 100, invoices: 100, clients: 100, featured: true },
    { title: t('subscription.premium'), price: 14, users: 10, quotes: 300, invoices: 300, clients: 300 },
  ];

  return (
    <Box id="overview" sx={{ width: '100%', color: publicPalette.text, backgroundColor: publicPalette.background }}>
      <MetaDecorator
        description={base.pageDescriptionRo}
        title={base.pageTitle}
        imageUrl={ContDePlataLogo_onwhite1}
        imageAlt={base.pageDescriptionRo}
        rel="https://contdeplata.md/"
      />

      <PublicBackdrop sx={{ pt: { xs: 4, md: 8 }, pb: { xs: 9, md: 14 } }}>
        <PublicContainer>
          <Grid container spacing={{ xs: 7, lg: 9 }} alignItems="center">
            <Grid item xs={12} lg={5.5}>
              <Stack
                spacing={3}
                alignItems={{ xs: 'center', lg: 'flex-start' }}
                textAlign={{ xs: 'center', lg: 'left' }}
              >
                <Typography
                  component="h1"
                  sx={{
                    maxWidth: 720,
                    fontSize: { xs: 44, sm: 60, lg: 76 },
                    lineHeight: 0.98,
                    fontWeight: 900,
                    letterSpacing: '-0.055em',
                    background: 'linear-gradient(115deg, #FFFFFF 8%, #BFEAFF 52%, #C8B8FF 92%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {t('base.title_hero')}
                </Typography>
                <Typography
                  sx={{ maxWidth: 650, color: publicPalette.muted, fontSize: { xs: 17, md: 20 }, lineHeight: 1.72 }}
                >
                  {t('base.subtitle_hero')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    component={NavLink}
                    to={`${appURL}/signup`}
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      px: 3.2,
                      py: 1.45,
                      color: '#03111A',
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontSize: 16,
                      fontWeight: 900,
                      background: `linear-gradient(110deg, ${publicPalette.primary}, ${publicPalette.accent})`,
                      boxShadow: `0 18px 42px ${alpha(publicPalette.primary, 0.22)}`,
                    }}
                  >
                    {t('button.signup_for_free')}
                  </Button>
                  <Button
                    component="a"
                    href="#workflow"
                    variant="outlined"
                    sx={{
                      px: 3.2,
                      py: 1.45,
                      color: publicPalette.text,
                      borderColor: publicPalette.border,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontSize: 16,
                      fontWeight: 800,
                      '&:hover': {
                        borderColor: alpha(publicPalette.primary, 0.6),
                        backgroundColor: alpha(publicPalette.primary, 0.05),
                      },
                    }}
                  >
                    {t('base.title_howitworks')}
                  </Button>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2.5 }} sx={{ pt: 1 }}>
                  {[
                    t('public.no_card', { defaultValue: 'No card required' }),
                    t('public.free_plan', { defaultValue: 'Free plan included' }),
                    t('public.setup_minutes', { defaultValue: 'Setup in minutes' }),
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={0.7} alignItems="center">
                      <CheckCircleRoundedIcon sx={{ color: publicPalette.accent, fontSize: 17 }} />
                      <Typography sx={{ color: publicPalette.muted, fontSize: 13 }}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={6.5}>
              <DashboardPreview t={t} />
            </Grid>
          </Grid>
        </PublicContainer>
      </PublicBackdrop>

      <Box
        sx={{
          borderTop: `1px solid ${publicPalette.border}`,
          borderBottom: `1px solid ${publicPalette.border}`,
          backgroundColor: '#070D19',
        }}
      >
        <PublicContainer sx={{ py: 3 }}>
          <Grid container spacing={2}>
            {[
              [<BoltRoundedIcon />, t('public.metric_fast', { defaultValue: 'Create in minutes' })],
              [<CloudDoneRoundedIcon />, t('public.metric_cloud', { defaultValue: 'Always available in cloud' })],
              [<LockRoundedIcon />, t('public.metric_secure', { defaultValue: 'Secure by design' })],
              [
                <CurrencyExchangeRoundedIcon />,
                t('public.metric_global', { defaultValue: 'Built for international trade' }),
              ],
            ].map(([icon, text]) => (
              <Grid item xs={12} sm={6} md={3} key={text}>
                <Stack
                  direction="row"
                  spacing={1.2}
                  alignItems="center"
                  justifyContent={{ xs: 'center', md: 'flex-start' }}
                  sx={{ color: publicPalette.muted }}
                >
                  <Avatar
                    sx={{
                      width: 35,
                      height: 35,
                      color: publicPalette.primary,
                      backgroundColor: alpha(publicPalette.primary, 0.09),
                    }}
                  >
                    {icon}
                  </Avatar>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{text}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </PublicContainer>
      </Box>

      <Box id="features" sx={{ py: { xs: 9, md: 14 } }}>
        <PublicContainer>
          <SectionHeading
            align="center"
            eyebrow={t('general.features')}
            title={t('public.features_title', { defaultValue: 'Everything your document workflow needs' })}
            description={t('public.features_text', {
              defaultValue:
                'A focused workspace for creating, sending, organizing, and tracking invoices and transport quotes without unnecessary complexity.',
            })}
            sx={{ mx: 'auto', mb: 7 }}
          />
          <Grid container spacing={2.5}>
            {features.map(([title, description], index) => (
              <Grid item xs={12} md={6} lg={4} key={title}>
                <GlassCard
                  sx={{
                    height: '100%',
                    p: 3.5,
                    transition: 'transform 180ms ease, border-color 180ms ease',
                    '&:hover': { transform: 'translateY(-6px)', borderColor: alpha(publicPalette.primary, 0.42) },
                  }}
                >
                  <Avatar
                    sx={{
                      mb: 2.5,
                      width: 48,
                      height: 48,
                      color: index % 2 ? publicPalette.secondary : publicPalette.primary,
                      backgroundColor: alpha(index % 2 ? publicPalette.secondary : publicPalette.primary, 0.11),
                    }}
                  >
                    {featureIcons[index]}
                  </Avatar>
                  <Typography component="h3" sx={{ mb: 1.2, color: publicPalette.text, fontSize: 20, fontWeight: 850 }}>
                    {title}
                  </Typography>
                  <Typography
                    sx={{
                      color: publicPalette.muted,
                      lineHeight: 1.75,
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {description}
                  </Typography>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </PublicContainer>
      </Box>

      <Box
        id="workflow"
        sx={{ py: { xs: 9, md: 14 }, background: `linear-gradient(180deg, #07101F, ${publicPalette.background})` }}
      >
        <PublicContainer>
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <SectionHeading
                eyebrow={t('general.how_it_works')}
                title={t('public.workflow_title', {
                  defaultValue: 'From registration to a sent invoice, without friction',
                })}
                description={t('public.workflow_text', {
                  defaultValue:
                    'A clear sequence guides your team from account setup to reusable customer records and professional documents.',
                })}
              />
              <Button
                component={NavLink}
                to={`${appURL}/signup`}
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{ mt: 3, px: 0, color: publicPalette.primary, textTransform: 'none', fontWeight: 900 }}
              >
                {t('button.start_now')}
              </Button>
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack spacing={1.5}>
                {workflow.map((item, index) => (
                  <GlassCard key={item} sx={{ p: 2.4 }}>
                    <Stack direction="row" spacing={2.2} alignItems="center">
                      <Typography
                        sx={{
                          minWidth: 45,
                          color: alpha(publicPalette.primary, 0.85),
                          fontSize: 14,
                          fontWeight: 900,
                          letterSpacing: '.12em',
                        }}
                      >
                        0{index + 1}
                      </Typography>
                      <Typography sx={{ color: publicPalette.text, fontSize: { xs: 15, sm: 17 }, fontWeight: 700 }}>
                        {item}
                      </Typography>
                    </Stack>
                  </GlassCard>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </PublicContainer>
      </Box>

      <Box sx={{ py: { xs: 9, md: 14 } }}>
        <PublicContainer>
          <GlassCard
            sx={{
              p: { xs: 3, md: 6 },
              background: `linear-gradient(135deg, ${alpha(publicPalette.primary, 0.1)}, ${alpha(publicPalette.secondary, 0.08)})`,
            }}
          >
            <Grid container spacing={5} alignItems="center">
              <Grid item xs={12} md={7}>
                <TechBadge>{t('public.security_badge', { defaultValue: 'Secure cloud foundation' })}</TechBadge>
                <Typography
                  component="h2"
                  sx={{
                    mt: 2.5,
                    mb: 2,
                    fontSize: { xs: 32, md: 48 },
                    lineHeight: 1.08,
                    fontWeight: 900,
                    letterSpacing: '-.04em',
                  }}
                >
                  {t('base.title_data_protection')}
                </Typography>
                <Typography sx={{ color: publicPalette.muted, fontSize: 17, lineHeight: 1.75 }}>
                  {t('base.subtitle_data_protection')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Stack spacing={1.5}>
                  {[
                    [<LockRoundedIcon />, t('public.security_access', { defaultValue: 'Protected account access' })],
                    [
                      <CloudDoneRoundedIcon />,
                      t('public.security_backup', { defaultValue: 'Cloud availability and backup' }),
                    ],
                    [
                      <AutoGraphRoundedIcon />,
                      t('public.security_control', { defaultValue: 'Clear document history and control' }),
                    ],
                  ].map(([icon, text]) => (
                    <Stack
                      key={text}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ p: 1.5, borderRadius: 2.5, backgroundColor: alpha('#FFFFFF', 0.035) }}
                    >
                      <Avatar sx={{ color: publicPalette.accent, backgroundColor: alpha(publicPalette.accent, 0.1) }}>
                        {icon}
                      </Avatar>
                      <Typography sx={{ fontWeight: 750 }}>{text}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </GlassCard>
        </PublicContainer>
      </Box>

      <Box id="pricing" sx={{ py: { xs: 9, md: 14 }, backgroundColor: '#070D19' }}>
        <PublicContainer>
          <SectionHeading
            align="center"
            eyebrow={t('general.price')}
            title={t('base.title_select_subscription')}
            description={t('base.subtitle_select_subscription')}
            sx={{ mx: 'auto', mb: 7 }}
          />
          <Grid container spacing={2.5} alignItems="stretch">
            {plans.map((plan, index) => (
              <Grid item xs={12} md={4} key={plan.title}>
                <GlassCard
                  sx={{
                    position: 'relative',
                    height: '100%',
                    p: 3.5,
                    borderColor: plan.featured ? alpha(publicPalette.primary, 0.65) : publicPalette.border,
                    transform: { md: plan.featured ? 'translateY(-12px)' : 'none' },
                    background: plan.featured
                      ? `linear-gradient(160deg, ${alpha(publicPalette.primary, 0.14)}, ${alpha(publicPalette.secondary, 0.08)})`
                      : undefined,
                  }}
                >
                  {plan.featured && (
                    <Chip
                      label={t('public.most_popular', { defaultValue: 'Most popular' })}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 18,
                        right: 18,
                        color: '#03111A',
                        fontWeight: 900,
                        backgroundColor: publicPalette.primary,
                      }}
                    />
                  )}
                  <Typography sx={{ color: publicPalette.text, fontSize: 22, fontWeight: 900 }}>
                    {plan.title}
                  </Typography>
                  <Stack direction="row" alignItems="flex-end" spacing={0.75} sx={{ my: 2.5 }}>
                    <Typography sx={{ fontSize: 50, lineHeight: 1, fontWeight: 900, letterSpacing: '-.05em' }}>
                      €{plan.price}
                    </Typography>
                    <Typography sx={{ color: publicPalette.muted, pb: 0.6 }}>
                      {t('subscription.slash_month')}
                    </Typography>
                  </Stack>
                  <Divider sx={{ mb: 2.5, borderColor: publicPalette.border }} />
                  <Stack spacing={1.4} sx={{ mb: 3.5 }}>
                    {[
                      [plan.users, t('subscription.users_included')],
                      [plan.quotes, t('subscription.quotes_included')],
                      [plan.invoices, t('subscription.invoices_included')],
                      [plan.clients, t('subscription.clients_included')],
                    ].map(([number, label]) => (
                      <Stack key={label} direction="row" spacing={1.2} alignItems="center">
                        <CheckCircleRoundedIcon sx={{ color: publicPalette.accent, fontSize: 19 }} />
                        <Typography sx={{ color: publicPalette.muted }}>
                          <Box component="span" sx={{ color: publicPalette.text, fontWeight: 850 }}>
                            {number}
                          </Box>{' '}
                          {label}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <Button
                    component={NavLink}
                    to={`${appURL}/signup`}
                    fullWidth
                    variant={plan.featured ? 'contained' : 'outlined'}
                    sx={{
                      py: 1.35,
                      borderRadius: 2.5,
                      color: plan.featured ? '#03111A' : publicPalette.text,
                      borderColor: publicPalette.border,
                      textTransform: 'none',
                      fontWeight: 900,
                      background: plan.featured
                        ? `linear-gradient(110deg, ${publicPalette.primary}, ${publicPalette.accent})`
                        : undefined,
                    }}
                  >
                    {t('button.signup_for_free')}
                  </Button>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
          <Typography sx={{ mt: 3, color: alpha(publicPalette.muted, 0.75), textAlign: 'center', fontSize: 13 }}>
            {t('public.pricing_note', {
              defaultValue: `Current plan limits are drawn from the application configuration (${maxNumberAllowedPerSubscription[0]}–${maxNumberAllowedPerSubscription[14]} records depending on plan).`,
            })}
          </Typography>
        </PublicContainer>
      </Box>

      <Box sx={{ py: { xs: 9, md: 13 } }}>
        <PublicContainer>
          <GlassCard
            sx={{
              p: { xs: 4, md: 7 },
              textAlign: 'center',
              background: `radial-gradient(circle at 50% 0%, ${alpha(publicPalette.primary, 0.17)}, transparent 52%), ${publicPalette.surface}`,
            }}
          >
            <Typography
              component="h2"
              sx={{
                mx: 'auto',
                maxWidth: 760,
                fontSize: { xs: 34, md: 54 },
                lineHeight: 1.08,
                fontWeight: 900,
                letterSpacing: '-.045em',
              }}
            >
              {t('base.title_try_it_now')}
            </Typography>
            <Typography
              sx={{
                mx: 'auto',
                mt: 2,
                mb: 3.5,
                maxWidth: 700,
                color: publicPalette.muted,
                fontSize: 18,
                lineHeight: 1.7,
              }}
            >
              {t('base.subtitle_try_it_now')}
            </Typography>
            <Button
              component={NavLink}
              to={`${appURL}/signup`}
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{
                px: 3.5,
                py: 1.5,
                color: '#03111A',
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 900,
                background: `linear-gradient(110deg, ${publicPalette.primary}, ${publicPalette.accent})`,
              }}
            >
              {t('button.try_for_free')}
            </Button>
          </GlassCard>
        </PublicContainer>
      </Box>

      <Footer />
      <ScrollTop>
        <Fab
          size="small"
          aria-label="Scroll to top"
          sx={{
            color: publicPalette.text,
            backgroundColor: alpha(publicPalette.surfaceRaised, 0.92),
            border: `1px solid ${publicPalette.border}`,
            '&:hover': { backgroundColor: publicPalette.surfaceRaised },
          }}
        >
          <KeyboardArrowUpRoundedIcon />
        </Fab>
      </ScrollTop>
    </Box>
  );
};

export default Base;
